import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, FileText, Settings, LogOut, BarChart3,
  Search, Filter, Send, CheckCircle, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInvoices = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editData, setEditData] = useState({ price: '', quantity: 1 });
  const [saving, setSaving] = useState(false);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API_URL}/api/admin/invoice${params}`, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [filterStatus]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openEditDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setEditData({ price: invoice.price || '', quantity: invoice.quantity || 1 });
  };

  const handleSaveAndSend = async () => {
    if (!selectedInvoice || !editData.price) {
      toast.error('Por favor introduce el precio');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/invoice/${selectedInvoice.invoice_request_id}`,
        { price: parseFloat(editData.price), quantity: parseInt(editData.quantity) },
        { withCredentials: true }
      );
      toast.success('Factura generada y enviada');
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Error al generar la factura');
    } finally {
      setSaving(false);
    }
  };

  const filteredInvoices = invoices.filter(i => 
    i.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.invoice_request_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.nif_cif_dni?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-invoices">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Dashboard</span>
          </Link>
          <Link to="/admin/warranties" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Garantías</span>
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 mb-1">
            <FileText className="w-5 h-5" />
            <span className="font-medium">Facturas</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Settings className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Configuración</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Gestión de Facturas</h1>
              <p className="text-[#52525B]">{invoices.length} solicitudes • Serie OLN</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <Input
                placeholder="Buscar por nombre, NIF, producto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="Pendiente">Pendientes</SelectItem>
                <SelectItem value="Completada">Completadas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F5F5F7] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Referencia</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Cliente</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">NIF/CIF</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Producto</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Estado</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice, index) => (
                      <tr 
                        key={invoice.invoice_request_id} 
                        className="border-b border-[#E5E7EB] hover:bg-[#F5F5F7]/50 transition-colors"
                        data-testid={`invoice-row-${index}`}
                      >
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm">
                            {invoice.invoice_number || `#${invoice.invoice_request_id?.slice(0, 8).toUpperCase()}`}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{invoice.full_name}</p>
                          <p className="text-xs text-[#A1A1AA]">{invoice.email}</p>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">
                          {invoice.nif_cif_dni}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{invoice.product_name}</p>
                          <p className="text-xs text-[#A1A1AA]">{invoice.purchase_source}</p>
                        </td>
                        <td className="px-4 py-3">
                          {invoice.status === 'Completada' ? (
                            <span className="status-badge status-completed flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              Completada
                            </span>
                          ) : (
                            <span className="status-badge status-pending flex items-center gap-1 w-fit">
                              <Clock className="w-3 h-3" />
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {invoice.status === 'Pendiente' ? (
                            <Button
                              size="sm"
                              className="bg-[#10B981] hover:bg-[#059669]"
                              onClick={() => openEditDialog(invoice)}
                              data-testid={`process-btn-${index}`}
                            >
                              Procesar
                              <Send className="w-3 h-3 ml-1" />
                            </Button>
                          ) : (
                            <span className="text-sm text-[#10B981] font-medium">
                              {invoice.price && `${(invoice.price * (invoice.quantity || 1) * 1.21).toFixed(2)} €`}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredInvoices.length === 0 && (
                <div className="p-12 text-center text-[#A1A1AA]">
                  No se encontraron facturas
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Process Invoice Dialog */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Procesar Factura
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 mt-4">
              {/* Info Summary */}
              <div className="bg-[#F5F5F7] rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#A1A1AA]">Cliente</p>
                    <p className="font-medium">{selectedInvoice.full_name}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">NIF/CIF/DNI</p>
                    <p className="font-medium font-mono">{selectedInvoice.nif_cif_dni}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Producto</p>
                    <p className="font-medium">{selectedInvoice.product_name}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Pedido</p>
                    <p className="font-medium font-mono">{selectedInvoice.order_id}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Lugar de Compra</p>
                    <p className="font-medium">{selectedInvoice.purchase_source}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Fecha de Compra</p>
                    <p className="font-medium">{selectedInvoice.purchase_date}</p>
                  </div>
                </div>
              </div>

              {/* Price Input */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Precio (sin IVA) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editData.price}
                    onChange={(e) => setEditData(prev => ({ ...prev, price: e.target.value }))}
                    className="mt-1"
                    placeholder="0.00"
                    data-testid="price-input"
                  />
                </div>
                <div>
                  <Label>Cantidad</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editData.quantity}
                    onChange={(e) => setEditData(prev => ({ ...prev, quantity: e.target.value }))}
                    className="mt-1"
                    data-testid="quantity-input"
                  />
                </div>
              </div>

              {/* Preview */}
              {editData.price && (
                <div className="bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-lg p-4">
                  <p className="text-sm text-[#52525B] mb-2">Vista previa:</p>
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>{(parseFloat(editData.price) * parseInt(editData.quantity || 1)).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>IVA (21%):</span>
                    <span>{(parseFloat(editData.price) * parseInt(editData.quantity || 1) * 0.21).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-[#0047FF]/20">
                    <span>Total:</span>
                    <span>{(parseFloat(editData.price) * parseInt(editData.quantity || 1) * 1.21).toFixed(2)} €</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setSelectedInvoice(null)}>
                  Cancelar
                </Button>
                <Button 
                  className="bg-[#10B981] hover:bg-[#059669]"
                  onClick={handleSaveAndSend}
                  disabled={saving || !editData.price}
                  data-testid="save-send-btn"
                >
                  {saving ? 'Generando...' : 'Guardar y Enviar'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInvoices;
